import { contact } from '../data/content';
import { useEmail } from '../context/EmailContext';

export default function Contact() {
  const { canEmail, handleQuoteRequest, loadingId } = useEmail();

  return (
    <div className="bg-background min-h-screen">
      {/* Page Header */}
      <div className="bg-gradient-to-br from-primary-dark via-primary to-primary-600 text-white py-20">
        <div className="container-wide">
          <span className="inline-block text-sm font-semibold text-primary-200 uppercase tracking-wider mb-3">
            Get in Touch
          </span>
          <h1 className="text-4xl md:text-5xl font-bold">Contact Us</h1>
          <p className="mt-4 text-primary-100 max-w-2xl text-lg">
            Ready to discuss your analytical testing needs? We're here to help.
          </p>
        </div>
      </div>

      {/* Contact Content */}
      <section className="section">
        <div className="container-narrow">
          <div className="card p-8 md:p-12 border border-gray-100">
            <p className="text-text-light leading-relaxed text-lg mb-10">
              We love hearing from our customers! Please don't hesitate to
              contact us with questions, quotes, or general feedback. Anikó can
              be reached either by email, phone, or at our address:
            </p>

            <div className="grid md:grid-cols-2 gap-8">
              {/* Left column - Contact info */}
              <div className="space-y-6 flex flex-col justify-between py-6">
                {/* Address */}
                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-primary-50 group-hover:bg-primary rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <svg
                      className="w-5 h-5 text-primary group-hover:text-white transition-colors"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text mb-1">
                      GAAS Analytical
                    </h3>
                    <p className="text-text-light text-sm">
                      {contact.address.street}
                      <br />
                      {contact.address.city}
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <a href={contact.phoneHref} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-primary-50 group-hover:bg-primary rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <svg
                      className="w-5 h-5 text-primary group-hover:text-white transition-colors"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text mb-1">Phone</h3>
                    <p className="text-text-light text-sm group-hover:text-primary transition-colors">
                      {contact.phone}
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a href={contact.emailHref} className="flex items-start gap-4 group">
                  <div className="w-12 h-12 bg-primary-50 group-hover:bg-primary rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                    <svg
                      className="w-5 h-5 text-primary group-hover:text-white transition-colors"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-text mb-1">Email</h3>
                    <p className="text-text-light text-sm group-hover:text-primary transition-colors">
                      {contact.email}
                    </p>
                  </div>
                </a>
              </div>

              {/* Right column - Social & CTA */}
              <div className="flex">
                <div className="bg-gradient-subtle rounded-2xl p-6 border border-gray-100 flex flex-col w-full">
                  <h3 className="font-semibold text-text mb-4">Follow Us</h3>
                  <div className="flex gap-3 mb-6">
                    <a
                      href={contact.social.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-white hover:bg-primary rounded-xl flex items-center justify-center shadow-soft hover:shadow-glow transition-all duration-200 group"
                      aria-label="LinkedIn"
                    >
                      <svg
                        className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
                      </svg>
                    </a>
                    <a
                      href={contact.social.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 bg-white hover:bg-primary rounded-xl flex items-center justify-center shadow-soft hover:shadow-glow transition-all duration-200 group"
                      aria-label="Twitter"
                    >
                      <svg
                        className="w-5 h-5 text-slate-600 group-hover:text-white transition-colors"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z" />
                      </svg>
                    </a>
                  </div>

                  {canEmail && (
                    <div className="border-t border-gray-200 pt-6 mt-auto">
                      <p className="text-sm text-text-light mb-4">
                        Ready to get started? Request a quote for your analytical testing needs.
                      </p>
                      <button
                        onClick={() => handleQuoteRequest(undefined, 'contact')}
                        disabled={loadingId === 'contact'}
                        className="btn-primary w-full justify-center relative"
                      >
                        <span className={`inline-flex items-center gap-2 ${loadingId === 'contact' ? 'invisible' : ''}`}>
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          Request a Quote
                        </span>
                        {loadingId === 'contact' && (
                          <svg className="w-5 h-5 animate-spin absolute" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
